import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { namespaces, useSharedTranslation } from 'us-react-components/dist/hooks/useSharedTranslation';
import { AxiosRequestConfig, MessageThreadService } from 'us-web-services';
import { faBars } from '@fortawesome/pro-light-svg-icons/faBars';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useDependencies } from '../../../dependencies';
import { Routes } from '../../../Routes';
import { Button } from '../..';
import { Avatar } from './Avatar';
import MainLinks from './MainLinks';
import { ProfileLinks } from './ProfileLinks';

const MobileMenu = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const { stores } = useDependencies();
  const navigate = useNavigate();
  const location = useLocation();
  const { firstName, id: userId } = stores.user.user!;
  const [open, setOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!userId) return;

    const config: AxiosRequestConfig = {
      params: { userId: userId.toString(), sitterId: userId.toString(), read: false, limit: 1 },
    };
    MessageThreadService.getByFilter(config).then((response): void => {
      setUnreadMessages(response.data.meta.size ?? 0);
    });
  }, [userId]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (e: MouseEvent | TouchEvent): void => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);

    return (): void => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  const handleToggle = (): void => setOpen(!open);

  const handleMessagesClick = (): void => {
    setOpen(false);
    navigate(generatePath(Routes.sitter.messages, { id: userId?.toString() ?? '' }));
  };

  return (
    <div ref={containerRef} className='relative'>
      <Button
        onClick={handleToggle}
        className='w-8 h-8 text-xl align-middle'
        aria-label={t('components.header.mobileMenu.toggle')}
        aria-expanded={open}
        aria-controls='mobile-menu-nav'
      >
        <FontAwesomeIcon icon={faBars} className='w-8 h-8' />
      </Button>
      <nav
        id='mobile-menu-nav'
        aria-hidden={!open}
        className={`${
          open ? 'visible opacity-100' : 'invisible opacity-0 pointer-events-none'
        } absolute right-0 top-full mt-2 w-64 bg-white border drop-shadow-md rounded text-left py-3 z-20 transition-opacity duration-200`}
      >
        <div className='flex items-center px-3 mb-2'>
          <Avatar className='!w-8' />
          <h3 className='inline-block align-middle mb-0 ml-3 text-base font-semibold'>
            {t('components.header.hello')} {firstName}
          </h3>
        </div>
        <hr className='border-neutral-200 my-2' />
        <MainLinks />
        <button
          type='button'
          onClick={handleMessagesClick}
          className='block w-full text-left py-1 px-3 cursor-pointer text-gray-700 hover:text-sapphire-700'
        >
          {unreadMessages > 0 && (
            <span aria-hidden='true' className='text-[#fe3824] mr-1'>&#9679;</span>
          )}
          {t('components.header.mainLinks.messages')}
          {unreadMessages > 0 && (
            <span className='sr-only'>
              {' '}
              {t('components.header.mainLinks.messagesUnread', { count: unreadMessages })}
            </span>
          )}
        </button>
        <hr className='border-neutral-200 my-2' />
        <h3 className='mb-0 px-3 text-base font-semibold'>{t('components.header.profile.account')}</h3>
        <ProfileLinks />
      </nav>
    </div>
  );
});

export default MobileMenu;
